import React, { useEffect, useMemo, useRef, useState } from 'react';
import pb from '../lib/pocketbase';

function HeroSlider({ pageTitle, pageSubtitle, image }) {
  const hasCustom = Boolean(image);
  const [dbSlides, setDbSlides] = useState([]);
  const [loadingDb, setLoadingDb] = useState(false);
  const latestReq = useRef(0);

  // Load slides from PocketBase if no custom single image is provided
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (hasCustom) return;
      setLoadingDb(true);
      try {
        const reqId = ++latestReq.current;
        const res = await pb.collection('hero_slides').getList(1, 200, {
          sort: '+order',
          filter: 'published = true',
        });
        if (cancelled || reqId !== latestReq.current) return;
        const urls = (res?.items || [])
          .map((rec) => (rec.image ? pb.files.getURL(rec, rec.image) : null))
          .filter(Boolean);
        setDbSlides(urls);
      } catch (e) {
        const msg = String(e?.message || '');
        if (/auto-?cancel/i.test(msg)) {
          // ignore PB auto-cancel; fallback will be used if needed
          return;
        }
        // swallow other errors; fallback will be used
      } finally {
        if (!cancelled) setLoadingDb(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [hasCustom]);

  const fallbackImages = useMemo(() => ([
    '/images/hero4.webp',
    '/images/hero5.webp',
    '/images/hero6.webp',
  ]), []);

  const images = useMemo(() => {
    if (hasCustom) return [image];
    if (dbSlides && dbSlides.length > 0) return dbSlides;
    return fallbackImages;
  }, [hasCustom, image, dbSlides, fallbackImages]);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const kickoffRef = useRef(null);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, 4500);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [images.length, paused]);

  // Démarrage initial après 3 secondes pour donner un premier mouvement
  useEffect(() => {
    if (images.length <= 1) return;
    kickoffRef.current = setTimeout(() => {
      if (!paused) {
        setIndex((i) => (i + 1) % images.length);
      }
    }, 3000);
    return () => {
      if (kickoffRef.current) clearTimeout(kickoffRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  const goNext = () => setIndex((i) => (i + 1) % images.length);
  const goPrev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const goTo = (i) => setIndex(i);

  return (
    <section
      className="hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {images.map((src, i) => (
        <div
          key={src}
          className={`hero-slide ${i === index ? 'hero-slide-active' : ''}`}
          style={{ backgroundImage: `url(${src})` }}
          aria-hidden={i !== index}
        />
      ))}
      {images.length > 1 && (
        <div className="hero-slider-nav" aria-hidden={false}>
          <button
            type="button"
            className="hero-arrow hero-arrow-left"
            aria-label="Slide précédent"
            onClick={goPrev}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
          >
            ‹
          </button>
          <button
            type="button"
            className="hero-arrow hero-arrow-right"
            aria-label="Slide suivant"
            onClick={goNext}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
          >
            ›
          </button>
        </div>
      )}
      <div className="hero-slider-overlay">
        <div className="hero-slider-badge">
          <div className="hero-badge-content" key={pageTitle}>
            <div className="hero-badge-title">{pageTitle}</div>
            {pageSubtitle ? (
              <div className="hero-badge-subtitle">{pageSubtitle}</div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSlider;
